import React, { useState } from 'react';
import { usePost } from 'hooks/use-post';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';

import { X } from 'lucide-react';
import Paper from 'components/paper';
import Divider from 'components/divider';
import Breadcrumbs from 'components/breadcrumbs';
import ShrimpDataUploadTabsHeader from './components/shrimp-data-upload-tabs-header';

import { ShrimpDataUploadType } from './types/shrimp-data-upload-type.enum';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { ProductionDataUpload, SanitationDataUpload } from './types/shrimp-data-upload.type';
import { postUploadProductionDataFile, postUploadSanitationDataFile } from './api/create-shrimp-data-file';

type Props = {};

type FileState = { file: File | null; error: string | null };

const initialFileState: FileState = { file: null, error: null };

const allowedTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
];

const ViewShrimpDataUpload: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const snackbarDispatch = useSnackbarDispatch();

  const [files, setFiles] = useState<Record<ShrimpDataUploadType, FileState>>({
    [ShrimpDataUploadType.Production]: initialFileState,
    [ShrimpDataUploadType.Sanitation]: initialFileState,
  });

  const [uploadingType, setUploadingType] = useState<ShrimpDataUploadType | null>(null);

  const setFileState = (type: ShrimpDataUploadType, state: FileState) => setFiles((prev) => ({ ...prev, [type]: state }));

  // #region POST DATA
  const uploadProductionFile = usePost<ProductionDataUpload, { fileName: string; file: File }>(
    (request) => postUploadProductionDataFile(request.fileName, request.file),
    () => {
      setUploadingType(null);
      setFileState(ShrimpDataUploadType.Production, initialFileState);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'shrimpDataUpload.uploadSuccess' }) },
      });
    },
    (error) => {
      setUploadingType(null);
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const uploadSanitationFile = usePost<SanitationDataUpload, { fileName: string; file: File }>(
    (request) => postUploadSanitationDataFile(request.fileName, request.file),
    () => {
      setUploadingType(null);
      setFileState(ShrimpDataUploadType.Sanitation, initialFileState);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      setUploadingType(null);
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  // #endregion POST DATA

  // #region HANDLERS
  const handleFileSelect = (type: ShrimpDataUploadType) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      setFileState(type, { file: null, error: intl.formatMessage({ id: 'shrimpDataUpload.fileTypeError' }) });
      return;
    }

    setFileState(type, { file, error: null });
  };

  const handleUpload = (type: ShrimpDataUploadType) => () => {
    const { file } = files[type];
    if (!file) return;

    setUploadingType(type);

    if (type === ShrimpDataUploadType.Sanitation) {
      uploadSanitationFile.mutate({ fileName: file.name, file });
    } else {
      uploadProductionFile.mutate({ fileName: file.name, file });
    }
  };

  const handleClear = (type: ShrimpDataUploadType) => (event: React.MouseEvent) => {
    event.preventDefault();
    setFileState(type, initialFileState);
  };
  // #endregion HANDLERS

  const renderFileZone = (type: ShrimpDataUploadType, inputId: string, chooseLabelId: string) => {
    const { file, error } = files[type];
    const isUploading = uploadingType === type;
    const otherType = type === ShrimpDataUploadType.Production ? ShrimpDataUploadType.Sanitation : ShrimpDataUploadType.Production;
    const otherHasFile = !!files[otherType].file;
    const isDisabled = isUploading || otherHasFile;

    return (
      <Box flex={1} display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} gap={2} borderRadius={2}>
        <input
          type="file"
          id={inputId}
          name={inputId}
          disabled={isDisabled}
          style={{ display: 'none' }}
          accept={allowedTypes.join(',')}
          onChange={handleFileSelect(type)}
        />
        <Typography variant="body1" fontWeight={600}>
          <FormattedMessage id={chooseLabelId} />
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 32 }}>
          {isUploading ? (
            <CircularProgress sx={{ color: theme.palette.common.white }} size={28} />
          ) : file ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </Typography>
              <IconButton size="small" onClick={handleClear(type)} sx={{ color: theme.palette.common.white }}>
                <X />
              </IconButton>
            </Box>
          ) : null}
        </Box>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
        <Button
          size="small"
          disabled={isDisabled}
          onClick={file ? handleUpload(type) : () => document.getElementById(inputId)?.click()}>
          <FormattedMessage id={file ? 'shrimpDataUpload.uploadButton' : 'shrimpDataUpload.chooseFile'} />
        </Button>
      </Box>
    );
  };

  return (
    <>
      <ShrimpDataUploadTabsHeader utils={<Breadcrumbs />} />
      <Paper transparent>
        <Box display={'flex'} gap={3}>
          {renderFileZone(ShrimpDataUploadType.Production, 'fileinput-production', 'shrimpDataUpload.chooseProductionFile')}
          <Divider orientation="vertical" flexItem />
          {renderFileZone(ShrimpDataUploadType.Sanitation, 'fileinput-sanitation', 'shrimpDataUpload.chooseSanitationFile')}
        </Box>
      </Paper>
    </>
  );
};

export default ViewShrimpDataUpload;
